// Jul-2026 (User-Wunsch) — Systempflege/Gefahrenzone als eigene Admin-Seite
// (vorher unten im Admin-Dashboard). Erreichbar über /admin/systempflege.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function AdminSystemMaintenancePage() {
    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-maintenance-page">
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#111827] mb-3" data-testid="admin-maintenance-back">
                <ChevronLeft className="w-3.5 h-3.5" /> Zurück zur Admin-Übersicht
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2 text-red-700 mb-1">
                <ShieldAlert className="w-7 h-7" /> Systempflege · Gefahrenzone
            </h1>
            <p className="text-sm text-gray-500 mb-6">Test-Resets & endgültige Löschungen — alle Aktionen sind NICHT umkehrbar.</p>
            <SystemMaintenanceSection />
        </div>
    );
}

// Jul-2026 (User-Wunsch): Admin setzt ALLE Fahrzeug-Verkäufe/Reservierungen zurück (mit Bestätigung).
function SystemMaintenanceSection() {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const runReset = async () => {
        setConfirmOpen(false);
        setRunning(true);
        try {
            const { data } = await api.post("/admin/reset-vehicle-sales");
            setResult(data);
            const delSum = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
            toast.success(`Zurückgesetzt: ${data.listings_status_reset} Fahrzeuge wieder verfügbar, ${delSum} Verkaufs-Datensätze gelöscht`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen");
        } finally { setRunning(false); }
    };

    return (
        <section data-testid="admin-maintenance-section">
            <div className="bg-white border-2 border-red-200 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-bold text-[#111827]">Fahrzeug-Verkäufe zurücksetzen</div>
                    <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">
                        Setzt ALLE Verkäufe &amp; Reservierungen (alle Händler) zurück: Fahrzeuge werden wieder
                        verfügbar, Verkaufsprozesse geleert, zugehörige Rechnungen, Belege, Transporte, Provisionen
                        und Rechnungszähler entfernt. Nicht umkehrbar!
                    </p>
                    {result && (
                        <p className="text-[11px] text-emerald-700 font-bold mt-1" data-testid="admin-reset-sales-result">
                            ✓ {result.listings_status_reset} Fahrzeuge wieder verfügbar · {result.listings_fields_cleared} Prozesse geleert · {Object.values(result.deleted || {}).reduce((a, b) => a + b, 0)} Datensätze gelöscht
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setConfirmOpen(true)}
                    disabled={running}
                    className="shrink-0 px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                    data-testid="admin-reset-sales-btn"
                >
                    {running ? "Läuft …" : "Verkäufe zurücksetzen"}
                </button>
            </div>
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="admin-reset-sales-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Alle Fahrzeug-Verkäufe zurücksetzen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Dies löscht bei ALLEN Händlern sämtliche Verkäufe, Reservierungen, Verkaufsprozesse,
                            Fahrzeug-Rechnungen und Belege. Die Fahrzeuge werden wieder als verfügbar angezeigt.
                            Diese Aktion kann NICHT rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-reset-sales-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={runReset} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-reset-sales-confirm">
                            Ja, alles zurücksetzen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {/* Jun-2026 (User-Wunsch) — Test-Daten endgültig löschen */}
            <PurgeToolCard
                title="Alle Aufgaben endgültig löschen"
                description="Löscht ALLE Mitarbeiter- und Händler-Aufgaben (offen + erledigt, alle Händler) endgültig aus der Datenbank. Für Test-Resets vor dem Go-Live. Nicht umkehrbar!"
                endpoint="/admin/purge-all-tasks"
                confirmText="Wirklich ALLE Aufgaben (alle Händler, offen + erledigt) endgültig löschen?"
                testId="admin-purge-tasks"
            />
            <PurgeToolCard
                title="Test-Kunden löschen"
                description="Löscht Kunden wie „Max Mustermann“, Demo-/Testkunden und komplett namenlose Datensätze („Kunde —“) inkl. deren verknüpfter Fahrzeuge. Erst Vorschau prüfen!"
                endpoint="/admin/purge-test-customers"
                confirmText="Alle gefundenen Test-Kunden (inkl. verknüpfter Fahrzeuge) endgültig löschen?"
                testId="admin-purge-test-customers"
                withPreview
            />
            <PurgeToolCard
                title="Kundenfahrzeuge ohne FIN löschen"
                description="Löscht alle Kundenfahrzeuge (Garage), bei denen KEINE Fahrgestellnummer hinterlegt ist. Erst Vorschau prüfen!"
                endpoint="/admin/purge-vinless-customer-vehicles"
                confirmText="Alle Kundenfahrzeuge ohne FIN endgültig löschen?"
                testId="admin-purge-vinless"
                withPreview
            />
            <PurgeToolCard
                title="Werkstattaufträge zurücksetzen"
                description="Löscht ALLE Werkstattaufträge (alle Händler, alle Status) inkl. Zeitbuchungen endgültig und setzt die Auftragsnummern auf 0 zurück — der nächste Auftrag beginnt wieder bei …-00001. Erst Vorschau prüfen!"
                endpoint="/admin/reset-work-orders"
                confirmText="Wirklich ALLE Werkstattaufträge endgültig löschen und den Nummernkreis auf 0 zurücksetzen?"
                testId="admin-reset-workorders"
                withPreview
            />
        </section>
    );
}

// Jun-2026 (User-Wunsch) — kompakte Gefahrenzone-Karte mit optionaler Vorschau (dry_run).
function PurgeToolCard({ title, description, endpoint, confirmText, testId, withPreview = false }) {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);

    const loadPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(endpoint, { dry_run: true });
            setPreview(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        setBusy(false);
    };
    const run = async () => {
        if (!window.confirm(confirmText)) return;
        setBusy(true);
        try {
            const { data } = await api.post(endpoint, { dry_run: false });
            setResult(data);
            setPreview(null);
            toast.success(`${data.deleted ?? 0} Datensätze endgültig gelöscht${data.vehicles_deleted ? ` (+${data.vehicles_deleted} Fahrzeuge)` : ""}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
        setBusy(false);
    };
    return (
        <div className="bg-white border-2 border-red-200 rounded-sm p-4 mt-3" data-testid={`${testId}-card`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-bold text-[#111827]">{title}</div>
                    <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">{description}</p>
                    {result && (
                        <p className="text-[11px] text-emerald-700 font-bold mt-1" data-testid={`${testId}-result`}>
                            ✓ {result.deleted ?? 0} gelöscht{result.vehicles_deleted ? ` · ${result.vehicles_deleted} verknüpfte Fahrzeuge entfernt` : ""}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    {withPreview && (
                        <button onClick={loadPreview} disabled={busy}
                            className="px-3 py-2 rounded-sm border border-gray-300 text-gray-700 text-xs font-bold uppercase tracking-wider hover:border-[#111827] disabled:opacity-50"
                            data-testid={`${testId}-preview-btn`}>
                            Vorschau
                        </button>
                    )}
                    <button onClick={run} disabled={busy || (withPreview && preview && preview.count === 0)}
                        className="px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                        data-testid={`${testId}-run-btn`}>
                        {busy ? "Läuft …" : "Endgültig löschen"}
                    </button>
                </div>
            </div>
            {preview && (
                <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`${testId}-preview`}>
                    <div className="text-xs font-bold text-red-700">{preview.count} Treffer würden gelöscht:</div>
                    <div className="text-[11px] text-gray-600 mt-1 max-h-32 overflow-y-auto">
                        {(preview.sample || []).map((s, i) => (
                            <div key={i}>{s.name}{s.customer_number ? ` · ${s.customer_number}` : ""}{s.email ? ` · ${s.email}` : ""}</div>
                        ))}
                        {preview.count > (preview.sample || []).length && <div className="text-gray-400">… und {preview.count - preview.sample.length} weitere</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
